'use client'
import { useMemo } from 'react'
import ErrorBoundary from '@error-boundary'
import dynamic from 'next/dynamic'

import { RaButton } from '@components/core/index.core'
import { useCart } from '@contexts/cart.context'
import { useSettings } from '@contexts/settings.context'
import { useUi } from '@contexts/ui.context'
import { base64Encode } from '@utilities/helpers/base64encode.helpers'
import { stripWpUrl, translate } from '@utilities/helpers/core.helpers'

const RaFormWrapper = dynamic(() =>
  import('@components/form-elements/RaFormWrapper/RaFormWrapper').then(
    ({ RaFormWrapper }) => RaFormWrapper
  )
)

export const RaShareTravelplan = () => {
  const cart = useCart()
  const ui = useUi()
  const { themeSettings } = useSettings()
  const label = translate('SAVE_AND_SHARE')
  const { socials, travelplan_page } = themeSettings

  const useForm = socials.form && socials.form.id > 0

  const travelPlanUrl = travelplan_page?.url

  const shareString = useMemo(() => {
    if (!cart.cartItems || cart.cartItems.length === 0) return ''
    const items = cart?.cartItems?.map(({ type, id, gid, country, parent }) => {
      return { postId: id, postType: type, gid, site: country, parent }
    })

    return base64Encode(items)
  }, [cart.cartItems])

  const travelPlanPath = stripWpUrl(travelPlanUrl)

  const shareSubject = socials.subject
  const shareContent = socials.body

  const handleShare = () => {
    const origin = window.location.origin
    const shareUrl = `${origin}${travelPlanPath}?travelitems=${shareString}`

    const emailWhitespace = '%3D%3D%0D%0A%0D%0A'
    const whatappWhitespace = '%0D'
    const emailShare = `mailto:?subject=${shareSubject}&body=${shareUrl}${emailWhitespace}${shareContent}`
    const whatsappShare = `https://api.whatsapp.com/send?text=${shareContent}.${whatappWhitespace}${shareUrl}`

    const formContent = (
      <RaFormWrapper
        id={socials.form.id}
        expires={socials.form.expires}
        history={socials.form.history}
        redirect={socials.form.redirect}
      />
    )
    const socialContent = (
      <ul style={{ display: 'flex', gap: 24, width: '100%' }}>
        {socials.socials.map((item, i) => {
          let href = '#'
          let label = ''
          switch (item.network) {
            case 'email':
              href = emailShare
              label = 'E-mail'
              break

            case 'whatsapp':
              href = whatsappShare
              label = 'WhatsApp'
              break
            default:
              href = '#'
              label = ''
              break
          }

          return (
            <li
              key={i}
              style={{ width: '100%' }}>
              <RaButton
                fullWidth
                link={{ url: href, title: label, target: '_blank' }}
                retainFullUrl
              />
            </li>
          )
        })}
      </ul>
    )

    ui.showModal({
      title: label,
      content: useForm ? formContent : socialContent,
    })
  }

  return (
    <ErrorBoundary>
      <RaButton
        variant="secondary-dark"
        label={label}
        onClick={handleShare}
        fullWidth
        className="social-network-label"
      />
    </ErrorBoundary>
  )
}
